import React from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";

const SEGMENTS = 4;

// Mirrors the signup policy: length >= 10 AND upper+lower AND digit AND symbol.
export const getPasswordScore = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return score;
};

const toneForScore = (score, theme) => {
  if (score <= 1) return theme.colors.accent.danger;
  if (score === 2) return theme.colors.accent.warning;
  if (score === 3) return theme.colors.accent.info;
  return theme.colors.accent.success;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
`;

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${SEGMENTS}, 1fr);
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const Segment = styled.span`
  height: ${({ theme }) => theme.spacing(0.75)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme, $filled, $tone }) =>
    $filled ? $tone : theme.colors.border.subtle};
  transition: background ${({ theme }) => theme.motion.base} ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  color: ${({ $tone }) => $tone};
`;

const PasswordStrengthMeter = ({ password }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!password) return null;

  const score = getPasswordScore(password);
  const tone = toneForScore(score, theme);
  const labels = {
    1: t("login.pw_strength.weak", { defaultValue: "Weak" }),
    2: t("login.pw_strength.fair", { defaultValue: "Fair" }),
    3: t("login.pw_strength.good", { defaultValue: "Good" }),
    4: t("login.pw_strength.strong", { defaultValue: "Strong" }),
  };
  const label = labels[score] || labels[1];

  return (
    <Wrap aria-live="polite">
      <Track aria-hidden="true">
        {Array.from({ length: SEGMENTS }).map((_, index) => (
          <Segment key={index} $filled={index < score} $tone={tone} />
        ))}
      </Track>
      <Label $tone={tone}>
        {t("login.pw_strength_label", { defaultValue: "Password strength" })}:{" "}
        {label}
      </Label>
    </Wrap>
  );
};

export default PasswordStrengthMeter;
